import { decodeAndStripHtml } from '@dupe/common/research-report'
import Link from 'next/link'
import { Fragment, type ReactNode } from 'react'

/**
 * Inline markdown parser for hub body text. The agent emits a deliberately small
 * grammar inside fields like `intro.body`, `guideSteps.steps[].description`,
 * `guidePathways.pathways[].body`, `faq.items[].answer`,
 * `topPicks.picks[].reason`, and `decisionBlock.supportText`. We render those
 * tokens deterministically so page copy can carry deep links into assigned
 * reports without opening a broader markdown surface.
 *
 * Constraints:
 *   - Only internal links (path starts with "/"). External URLs are stripped
 *     and the label is rendered as plain text.
 *   - Supported inline marks are `[label](/path#anchor)`, `**bold**`, and
 *     `*italic*`. Underline, code, lists, headings, and raw HTML pass through.
 *   - Paragraph splitting on \n\n is the caller's responsibility.
 */

const INLINE_TOKEN_REGEX =
  /\[([^\[\]]+)\]\(([^()\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g

export interface RichTextSegment {
  type: 'text' | 'link' | 'strong' | 'em'
  text: string
  href?: string
  start: number
}

export function parseRichTextSegments(input: string): RichTextSegment[] {
  const segments: RichTextSegment[] = []
  let cursor = 0

  for (const match of input.matchAll(INLINE_TOKEN_REGEX)) {
    const [whole, linkLabel, href, strongText, emText] = match
    if (!whole || match.index === undefined) continue

    if (match.index > cursor) {
      segments.push({
        type: 'text',
        text: input.slice(cursor, match.index),
        start: cursor,
      })
    }

    if (linkLabel && href) {
      if (href.startsWith('/') && !href.startsWith('//')) {
        segments.push({
          type: 'link',
          text: linkLabel,
          href,
          start: match.index,
        })
      } else {
        // External href (or otherwise unsafe) — drop the link, keep the label.
        segments.push({ type: 'text', text: linkLabel, start: match.index })
      }
    } else if (strongText) {
      segments.push({ type: 'strong', text: strongText, start: match.index })
    } else if (emText) {
      segments.push({ type: 'em', text: emText, start: match.index })
    } else {
      segments.push({ type: 'text', text: whole, start: match.index })
    }

    cursor = match.index + whole.length
  }

  if (cursor < input.length) {
    segments.push({ type: 'text', text: input.slice(cursor), start: cursor })
  }

  return segments
}

export function renderRichText(input: string): ReactNode {
  const segments = parseRichTextSegments(input)
  if (segments.length === 0) return decodeAndStripHtml(input)

  return segments.map((segment) => {
    const key = `rich-${segment.start}-${segment.type}`
    // FAQ answers and other hub body copy occasionally carry HTML entities
    // (`&amp;`, `&#39;`) or stray tags from upstream sources; decode + strip so
    // React does not render them as literal escaped text. Markdown structure is
    // already resolved from the raw string above, so this only touches display
    // text, never the inline grammar.
    const text = decodeAndStripHtml(segment.text)
    if (segment.type === 'link' && segment.href) {
      return (
        <Link
          key={key}
          href={segment.href}
          className='text-info-700 underline-offset-4 hover:underline'
        >
          {text}
        </Link>
      )
    }
    if (segment.type === 'strong') {
      return <strong key={key}>{text}</strong>
    }
    if (segment.type === 'em') {
      return <em key={key}>{text}</em>
    }
    return <Fragment key={key}>{text}</Fragment>
  })
}

export function renderRichTextParagraphs(input: string): ReactNode[] {
  return input.split(/\n\n+/).flatMap((rawParagraph) => {
    const paragraph = rawParagraph.trim()
    if (!paragraph) return []
    return [
      <p
        key={`para-${paragraph.length}-${paragraph.slice(0, 32)}`}
        className='text-base leading-7 text-foreground'
      >
        {renderRichText(paragraph)}
      </p>,
    ]
  })
}
